import styled, { css } from 'styled-components';

import { darken, rgba } from 'polished';

type FilterProps = {
  isActive: boolean;
};

type StatusFieldProps = {
  color: string;
};

export const Container = styled.div`
  font-family: 'Sora', sans-serif;
  padding: 50px;
  display: flex;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.white};

  * {
    letter-spacing: 0px;
    line-height: initial;
  }

  @media only screen and (max-width: ${({ theme }) => theme.breakpoints.xxl}px) {
    padding: 30px;
  }

  @media only screen and (max-width: ${({ theme }) => theme.breakpoints.xl}px) {
    padding: 20px;
  }
`;

export const Wrapper = styled.div`
  max-width: 1500px;
  width: 100%;
  position: relative;

  > header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 60px;

    @media only screen and (max-width: ${({ theme }) => theme.breakpoints.lg}px) {
      flex-direction: column;
      justify-content: center;
      text-align: center;

      > span {
        padding-top: 20px;
      }

      > button {
        margin-top: 15px;
      }
    }

    h1 {
      font-weight: 700;
      color: ${({ theme }) => theme.colors.black};
      font-size: 28px;
      letter-spacing: 0px;
    }

    h3 {
      color: ${({ theme }) => theme.colors.grey};
      font-weight: 400;
      padding-top: 8px;
    }

    span {
      color: ${({ theme }) => theme.colors.softBlack};

      strong {
        font-weight: 700;
      }
    }

    button {
      display: flex;
      align-items: center;
      padding: 13px 18px;
      border-radius: 5px;
      background-color: ${({ theme }) => theme.colors.main};
      border: none;
      transition: background-color 0.2s ease;
      white-space: nowrap;
      padding-left: 20px;

      span {
        font-family: 'Sora', sans-serif;
        font-size: 15px;
        font-weight: 600;
        color: ${({ theme }) => theme.colors.white};
      }

      &:hover {
        background-color: ${({ theme }) => darken(0.03, theme.colors.main)};
      }

      &:hover {
        background-color: ${({ theme }) => darken(0.03, theme.colors.main)};
      }

      &.outline {
        background-color: ${({ theme }) => theme.colors.white};
        border: 1px solid ${({ theme }) => theme.colors.main};

        span {
          color: ${({ theme }) => theme.colors.main};
        }

        &:hover {
          background-color: ${({ theme }) => rgba(theme.colors.main, 0.1)};
        }
      }

      & + button {
        margin-left: 10px;
      }
    }
  }
`;

export const MenuTable = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  height: 60px;
  background: none;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
  margin-bottom: 30px;
  overflow-x: auto;

  div {
    display: flex;
    align-items: center;

    button {
      svg {
        margin-right: 7px;
      }
    }

    input {
      width: 250px;
      border: none;
      background: none;
      padding: 15px 15px;
      border-radius: 6px;

      &::placeholder {
        color: ${({ theme }) => darken(0.2, theme.colors.lightGrey)};
      }
    }

    svg {
      color: ${({ theme }) => theme.colors.grey};
      font-size: 25px;
    }
  }

  ul {
    display: flex;
    height: 100%;

    li {
      & + li {
        margin-left: 20px;
      }

      button {
        height: 100%;
        background: none;
        align-items: center;
        padding: 0 20px;
        font-size: 17px;
        font-weight: 500;

        &:hover {
          color: ${({ theme }) => theme.colors.main};
          border-bottom-color: ${({ theme }) => theme.colors.main};
        }
      }
    }
  }
`;

export const Filter = styled.button<FilterProps>`
  border: none;
  color: ${({ theme }) => theme.colors.grey};
  border-bottom: 3px solid transparent;

  ${({ isActive }) => {
    if (isActive) {
      return css`
        color: ${({ theme }) => theme.colors.main};
        border-bottom-color: ${({ theme }) => theme.colors.main};
      `;
    }
    return null;
  }};
`;

export const Table = styled.table`
  width: 100%;
  max-width: 100%;
  border-collapse: collapse;

  overflow-x: auto;

  thead {
    th {
      font-weight: 500;
      color: #acb3be;
      text-align: left;
      padding: 20px 25px;
      text-transform: uppercase;
      font-size: 14px;

      &:first-child {
        padding-left: 30px;
      }

      &:last-child {
        text-align: center;
      }
    }
  }

  tbody {
    tr {
      background-color: #ffffff;
      border: 2px solid #e3e4e8;

      & + tr {
        padding-bottom: 1em;
      }

      td {
        padding: 20px 25px;
        color: #271b16;
        font-weight: 500;
        font-size: 14px;
        height: 35px;

        &:last-child {
          text-align: center;
        }
      }
    }
  }
`;

export const StatusField = styled.div<StatusFieldProps>`
  span {
    ${({ color }) => css`
      background: ${rgba(color, 0.075)};
      color: ${color};
    `}

    text-align: center;
    text-transform: uppercase;
    padding: 9px 21px;
    border-radius: 2px;
    font-size: 14px;
    font-weight: 600;
  }
`;

export const Pagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 0px;

  flex-direction: column;
  gap: 8px;

  @media (min-width: 650px) {
    flex-direction: row;
  }

  p {
    strong {
      font-weight: 600;
    }
  }

  div {
    display: flex;
    align-items: center;
    flex-direction: row;

    span {
      margin: 0 10px 2px;
    }

    p {
      margin-right: 20px;
    }

    button {
      display: flex;
      border: none;
      background-color: ${({ theme }) => theme.colors.main};
      align-items: center;
      justify-content: center;
      width: 25px;
      height: 25px;
      border-radius: 5px;

      svg {
        font-size: 18px;
        color: ${({ theme }) => theme.colors.white};
      }

      &:disabled {
        background-color: ${({ theme }) => theme.colors.grey};
        cursor: not-allowed;
        opacity: 0.5;
      }
    }
  }
`;
